import { actionCreatorFactory, select, Store, } from "@knyt/clerk";
import { dom, html, } from "@knyt/weaver";
import { DEFAULT_NAME_PREFIX } from "./constants.js";
import { cssIncludeToString } from "./cssIncludeToString.js";
import { cssStyleSheetToString } from "./cssStyleSheetToString.js";
import { getCSSStyleSheetConstructor } from "./getCSSStyleSheetConstructor.js";
import { serializeCSSObject } from "./serializeCSSObject.js";
function parseRuleNameInput(input) {
    const pieces = input.split(":");
    const isPseudo = pieces.length > 1;
    const [ruleName, ...pseudoPieces] = pieces;
    const pseudoSuffix = isPseudo ? `:${pseudoPieces.join(":")}` : "";
    return {
        ruleName: ruleName,
        pseudoSuffix,
    };
}
/**
 * A list of selector names that should be treated as verbatim selectors.
 */
const verbatimSelectorNames = [":root", ":host"];
const verbatimPrefixes = ["::slotted(", ":host-context("];
function isVerbatimSelector(input) {
    return (verbatimSelectorNames.includes(input) ||
        verbatimPrefixes.some((selectorName) => input.startsWith(selectorName)));
}
function createSelectorCreator(input) {
    if (isVerbatimSelector(input)) {
        return () => input;
    }
    const { ruleName, pseudoSuffix } = parseRuleNameInput(input);
    return (serializedNames) => {
        return `.${serializedNames[ruleName]}${pseudoSuffix}`;
    };
}
function getInputSelector(ruleName, input) {
    if ("selector" in input) {
        const selector = input.selector;
        return typeof selector === "function" ? selector : () => selector;
    }
    return createSelectorCreator(ruleName);
}
function getInputCSSObject(input) {
    if ("selector" in input) {
        return input.styles;
    }
    return input;
}
// NOTE: Nested rule support is not implemented here to preserve type safety.
// TODO: Consider enhancing CSS support directly rather than extending style objects for this.
function inputToRulePayloads(ruleName, input) {
    return [
        {
            ruleName,
            selectorCreator: getInputSelector(ruleName, input),
            styles: getInputCSSObject(input),
        },
    ];
}
/**
 * A class that represents a CSS style sheet.
 *
 * @public
 */
export class StyleSheet extends Store {
    static fromCSS(input, options) {
        const styleSheet = new StyleSheet(options);
        styleSheet.include(input);
        return styleSheet;
    }
    /**
     * Create a new `StyleSheet` from a set of rules.
     *
     * @remarks
     *
     * Despite the rules being passed as an object, the order of the rules is
     * maintained in the stylesheet. This is important for specificity.
     */
    /*
     * ### Private Remarks
     *
     * I decided to use an object for the rules, because it's easier to work with
     * and more readable. The order of the rules is maintained by using `Object.entries`
     * to iterate over the rules.
     */
    static fromRules(rules, options) {
        const styleSheet = new StyleSheet(options);
        for (const entry of Object.entries(rules)) {
            const [ruleName, input] = entry;
            const payloads = inputToRulePayloads(ruleName, input);
            for (const payload of payloads) {
                styleSheet.addRule(payload);
            }
        }
        return styleSheet;
    }
    static fromCSSStyleSheet(cssStyleSheet, options) {
        return this.fromCSS(cssStyleSheetToString(cssStyleSheet), options);
    }
    /**
     * @deprecated Use `isStyleSheet` directly instead.
     */
    static isStyleSheet = isStyleSheet;
    /**
     * The document to use for rendering.
     */
    #document;
    /**
     * Whether to disable server-side rendering.
     *
     * @see {@link StyleSheet.Options.disableSSR}
     */
    #disableSSR;
    // Maintains a strong reference to the subscription to retain the subscriber
    #subscription;
    #replacementPromise;
    #queuedCSSUpdate;
    constructor(options = {}) {
        super({
            addedRules: [],
            cssObjectHashBySelectorCreator: new Map(),
            includedCSS: [],
            serializedCache: {},
            serializedNamesByRuleName: {},
        });
        this.#document = options.document ?? globalThis.document;
        this.#disableSSR = options.disableSSR ?? false;
        // Maintains a strong reference to the subscription to retain the subscriber
        this.#subscription = this.onCSSChange(this.#handleCSSChange);
    }
    #handleCSSChange = async (css) => {
        if (this.#replacementPromise) {
            this.#queuedCSSUpdate = css;
            return;
        }
        try {
            // Update the CSSStyleSheet, if it exists, with the new CSS.
            // The update is performed asynchronously after the initial render.
            this.#replacementPromise = this.#cssStyleSheet?.replace(css);
            await this.#replacementPromise;
        }
        catch (error) {
            console.error("Error updating CSSStyleSheet:", error);
        }
        finally {
            this.#replacementPromise = undefined;
        }
        if (this.#queuedCSSUpdate === undefined)
            return;
        const queuedCSS = this.#queuedCSSUpdate;
        this.#queuedCSSUpdate = undefined;
        this.#handleCSSChange(queuedCSS);
    };
    /**
     * Prefix for serialized names.
     */
    // TODO: Expose this as an option.
    #namePrefix = DEFAULT_NAME_PREFIX;
    addRule = this.bindActionCreator(Actions.addRule);
    /**
     * Adds the given CSS to the style sheet.
     *
     * @remarks
     *
     * To clarify, this method does not create an @import rule,
     * instead it copies the CSS string from the input and includes it
     * in the style sheet as a string. This is useful for including
     * supplementary CSS such as animations and fonts.
     */
    // TODO: Refactor to not use `bindActionCreator`
    include = this.bindActionCreator(Actions.include);
    #createSerializedName = (hash) => {
        return `${this.#namePrefix}-${hash}`;
    };
    /**
     * @internal scope: package
     */
    reduce(state, action) {
        if (Actions.addRule.match(action)) {
            const nextState = { ...state };
            const payload = action.payload;
            nextState.addedRules = [
                ...state.addedRules,
                payload,
            ];
            const { ruleName, selectorCreator, styles } = payload;
            const serialized = payload.serialized ?? serializeCSSObject(this.#document, styles);
            const { declarationBlock, hash } = serialized;
            const name = this.#createSerializedName(hash);
            if (!nextState.serializedCache[hash]) {
                nextState.serializedCache = {
                    ...state.serializedCache,
                    [hash]: declarationBlock,
                };
            }
            nextState.serializedNamesByRuleName = {
                ...state.serializedNamesByRuleName,
                [ruleName]: name,
            };
            nextState.cssObjectHashBySelectorCreator.set(selectorCreator, hash);
            return nextState;
        }
        if (Actions.include.match(action)) {
            const input = action.payload;
            const cssText = cssIncludeToString(input);
            return {
                ...state,
                includedCSS: [...state.includedCSS, cssText],
            };
        }
        return state;
    }
    #getSerializedCSSObject(hash) {
        return this.value.serializedCache[hash];
    }
    #isSerializedCSSObjectCached(serializedCSSObject) {
        return this.#getSerializedCSSObject(serializedCSSObject.hash) !== undefined;
    }
    sx = (styles) => {
        const serialized = serializeCSSObject(this.#document, styles);
        const { declarationBlock, hash } = serialized;
        const name = this.#createSerializedName(hash);
        // If the styles are already registered, we don't need to register a new rule.
        // We check if the styles are already registered, because `sx` is typically used
        // for adding rules on the fly during rendering, and we don't want to generate
        // a register a new CSS rule for each render.
        //
        // While, the class name is deterministic based on the styles, so we can reuse the same
        // class name for the same styles, but we need to only register the rule once.
        // Otherwise, the stylesheet would grow indefinitely.
        if (this.#isSerializedCSSObjectCached({ hash, declarationBlock })) {
            return name;
        }
        const ruleName = `${name}-rule`;
        const selectorCreator = createSelectorCreator(ruleName);
        this.addRule({
            ruleName,
            selectorCreator,
            serialized,
            styles,
        });
        return name;
    };
    #selectCSSObjectHashBySelectorCreator = (state) => {
        return state.cssObjectHashBySelectorCreator;
    };
    #selectSerializedNamesByRuleName = (state) => {
        return state.serializedNamesByRuleName;
    };
    #selectSerializedCache = (state) => {
        return state.serializedCache;
    };
    #selectClassNames = (state) => {
        // The type assertion is necessary, because the despite the initial state,
        // being an empty object, `serializedNamesByRuleName` should
        // be a complete dictionary of class names by rule name, by the time the
        // state is consumed.
        //
        // This is not a codified guarantee, but it is a design expectation,
        // that the class names are always complete after a stylesheet is created.
        // TODO: Perhaps we should codify this guarantee by marking stylesheets as "sealed"
        // after they are created.
        return state.serializedNamesByRuleName;
    };
    #selectRulesCSS = select(this.#selectSerializedCache, this.#selectCSSObjectHashBySelectorCreator, this.#selectClassNames).combine((serializedCache, cssObjectHashBySelectorCreator, classNames) => {
        return Array.from(cssObjectHashBySelectorCreator.entries())
            .map(([selectorCreator, hash]) => {
            const selector = selectorCreator(classNames);
            const styles = serializedCache[hash];
            return `${selector} { ${styles} }`;
        })
            .join("\n");
    });
    #selectIncludedCSS(state) {
        return state.includedCSS.join("\n");
    }
    #selectCSS = select(this.#selectRulesCSS, this.#selectIncludedCSS).combine((rulesCSS, includedCSS) => [includedCSS, rulesCSS].join("\n").trim() + "\n");
    /*
     * ### Private Remarks
     *
     * This is conceptually readonly, because the same instance of the `CSSStyleSheet` should be used
     * for the lifetime of the stylesheet. This is because the `CSSStyleSheet` is a live
     * object that is attached to the document, and it should not be replaced.
     *
     * The design decision to support that if the CSS changes, the `CSSStyleSheet` should
     * be updated, is to allow for the stylesheet to be updated in place, without replacing
     * the `CSSStyleSheet` instance.
     */
    #cssStyleSheet;
    toCSSStyleSheet(CSSStyleSheetConstructor = globalThis.CSSStyleSheet) {
        // We lazily create the CSSStyleSheet, because it should only be created
        // when it is needed.
        //
        // This allows for the stylesheet to be created and manipulated without
        // creating a `CSSStyleSheet` instance which may not be used, and may cause
        // unnecessary work and errors.
        //
        // Specifically, `@import` rules may be used
        // in our `Stylesheet`, but a warning will be thrown if the `@import` rule
        // is used in `CSSStyleSheet`.
        //
        // A common use case is to use the `Stylesheet` to generate CSS for @font-face
        // rules that rely on `@import` rules to load the font files, but instead of using
        // `CSSStyleSheet` to apply the rules, the `Stylesheet` is used to generate the CSS
        // which can then be rendered as a `style` element, using the `StyleSheet.style` method.
        if (!this.#cssStyleSheet) {
            if (!CSSStyleSheetConstructor) {
                throw new Error("CSSStyleSheet is not supported in this environment. Please provide a CSSStyleSheet constructor.");
            }
            this.#cssStyleSheet = new CSSStyleSheetConstructor();
            // For the initial render, we synchronously replace the CSS.'
            try {
                this.#cssStyleSheet.replaceSync(this.toCSSString());
            }
            catch (error) {
                console.error(error);
            }
        }
        return this.#cssStyleSheet;
    }
    /**
     * Subscribe to changes to the CSS.
     */
    onCSSChange = this.createSubscriptionFactory(this.#selectCSS);
    /**
     * A dictionary of class names by rule name.
     *
     * @public
     */
    /*
     * ### Private Remarks
     *
     * This should return an object literal that can be safety enumerated.
     */
    get classNames() {
        return this.#selectClassNames(this.value);
    }
    toCSSString() {
        return this.#selectCSS(this.value);
    }
    toString() {
        return this.toCSSString();
    }
    #renderStyleDom() {
        return dom.style.$innerHTML(this.toCSSString());
    }
    #renderStyleHtml() {
        return html.style.$innerHTML(this.toCSSString());
    }
    get style() {
        const style = () => {
            return this.#renderStyleDom();
        };
        style.html = () => {
            return this.#renderStyleHtml();
        };
        return style;
    }
    hostRender() {
        return this.#disableSSR ? null : this.#renderStyleHtml();
    }
    extend(mixin, options) {
        if (!mixin) {
            return this;
        }
        const styleSheet = new StyleSheet(options);
        const addedRules = this.value.addedRules;
        const mixinRules = [];
        const entries = Object.entries(mixin);
        for (const entry of entries) {
            const [ruleName, input] = entry;
            const payloads = inputToRulePayloads(ruleName, input);
            for (const payload of payloads) {
                mixinRules.push(payload);
            }
        }
        const mergedRules = [];
        for (const addedRule of addedRules) {
            const { ruleName, selectorCreator, styles } = addedRule;
            const matchingMixinRule = mixinRules.find((mixinRule) => mixinRule.ruleName === ruleName);
            if (matchingMixinRule) {
                const { styles: mixinStyles } = matchingMixinRule;
                mergedRules.push({
                    ruleName,
                    selectorCreator,
                    styles: { ...styles, ...mixinStyles },
                });
            }
            else {
                mergedRules.push(addedRule);
            }
        }
        for (const mixinRule of mixinRules) {
            const { ruleName } = mixinRule;
            if (!addedRules.some((addedRule) => addedRule.ruleName === ruleName)) {
                mergedRules.push(mixinRule);
            }
        }
        for (const rule of mergedRules) {
            styleSheet.addRule(rule);
        }
        return styleSheet;
    }
    clone(options) {
        const nextOptions = { document: this.#document, ...options };
        const styleSheet = new StyleSheet(nextOptions);
        for (const rule of this.value.addedRules) {
            styleSheet.addRule(rule);
        }
        return styleSheet;
    }
    #selectAddedRules = (state) => state.addedRules;
    #selectRuleNames = select(this.#selectAddedRules).combine((addedRules) => addedRules.map((rule) => rule.ruleName));
    /**
     * Creates a comparison string of the CSS.
     * This is used to compare stylesheets for equality.
     */
    #selectComparisonString = select(this.#selectRuleNames, this.#selectCSS).combine((ruleNames, css) => `${ruleNames}${css}`);
    /**
     * A string that can be used to compare this style sheet to another.
     *
     * @remarks
     *
     * This string is a combination of the rule names and the CSS.
     * It can be used to determine if two style sheets are effectively equal.
     *
     * This is useful for caching and preventing unnecessary updates to the DOM.
     *
     * @internal scope: package
     *
     * This is considered a private API, but it is exposed with an underscore
     * so that the `equals` method can easily access it.
     */
    get _comparisonString() {
        return this.#selectComparisonString(this.value);
    }
    /**
     * Determines if the given style sheet is effectively equal to this one.
     *
     * @remarks
     *
     * Two style sheets are considered equal if they have the same rules in the same order,
     * and the same CSS.
     *
     * This is useful for determining if a style sheet has changed, and can be used
     * to prevent unnecessary updates to the DOM.
     */
    equals(other) {
        if (!other)
            return false;
        return this === other || this._comparisonString === other._comparisonString;
    }
    /**
     * Adds the style sheet to the document or shadow root
     * if it isn't already added.
     */
    addTo(documentOrShadow) {
        const cssStyleSheet = this.toCSSStyleSheet(getCSSStyleSheetConstructor(documentOrShadow));
        if (!documentOrShadow.adoptedStyleSheets.includes(cssStyleSheet)) {
            documentOrShadow.adoptedStyleSheets.push(cssStyleSheet);
        }
    }
    /**
     * Removes the first instance of the style sheet from the document or shadow root.
     */
    removeFrom(documentOrShadow) {
        const cssStyleSheet = this.toCSSStyleSheet(getCSSStyleSheetConstructor(documentOrShadow));
        const indexToRemove = documentOrShadow.adoptedStyleSheets.indexOf(cssStyleSheet);
        if (indexToRemove === -1)
            return;
        documentOrShadow.adoptedStyleSheets =
            documentOrShadow.adoptedStyleSheets.toSpliced(indexToRemove, 1);
    }
}
/**
 * Checks if the given value is a `StyleSheet` using duck typing.
 *
 * @remarks
 *
 * This is useful for checking if a value is a `StyleSheet` without
 * using `instanceof`, which would prevent multiple versions of the
 * `StyleSheet` class from being used in the same project.
 */
export function isStyleSheet(value) {
    return (typeof value === "object" &&
        value !== null &&
        "sx" in value &&
        typeof value.sx == "function" &&
        "toCSSStyleSheet" in value &&
        typeof value.toCSSStyleSheet == "function" &&
        "onCSSChange" in value &&
        typeof value.onCSSChange == "function");
}
// TODO: Migrate this to `Store.createActions()`
var Actions;
(function (Actions) {
    const createAction = actionCreatorFactory("StyleSheet");
    Actions.addRule = createAction("addRule");
    Actions.include = createAction("include");
})(Actions || (Actions = {}));
